import React, { useEffect, useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { User, Shield, Bell, Save, Key, BellRing, BellOff, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { 
  isPushSupported, 
  getPermissionStatus, 
  subscribeToPush, 
  unsubscribeFromPush, 
  isSubscribed,
  initializePushNotifications 
} from '../../lib/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    contact_number: '',
    preferences: {
      dark_mode: false,
      notifications_enabled: true,
      sound_enabled: true,
      auto_refresh: true
    }
  });
  const [sessionInfo, setSessionInfo] = useState(null);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [vapidKey, setVapidKey] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [testingPush, setTestingPush] = useState(false);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile({
          name: data.name || '',
          email: data.email || '',
          contact_number: data.profile?.contact_number || '',
          preferences: data.profile?.preferences || {
            dark_mode: false,
            notifications_enabled: true,
            sound_enabled: true,
            auto_refresh: true
          }
        });
      }
    } catch (error) {
      console.error('Failed to load profile', error);
    }
  };

  const fetchSessionInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session-info`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionInfo(data);
      }
    } catch (error) {
      console.error('Failed to load session info', error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchSessionInfo();
    initPushNotifications();
  }, []);

  const initPushNotifications = async () => {
    const status = await initializePushNotifications();
    setPushSupported(status.supported);
    setPushSubscribed(status.subscribed || false);
    setPushPermission(status.permission || 'default');
    
    // Get VAPID key
    try {
      const response = await fetch(`${API_URL}/api/platform/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const settings = await response.json();
        setVapidKey(settings?.web_push?.vapid_public_key);
      }
    } catch (error) {
      console.log('Could not fetch VAPID key');
    }
  };

  const handleTogglePush = async () => {
    if (!pushSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        toast.success('Unsubscribed from push notifications');
      } else {
        if (!vapidKey) {
          toast.error('Push notifications are not configured. Please contact admin.');
          return;
        }
        await subscribeToPush(vapidKey);
        setPushSubscribed(true);
        setPushPermission('granted');
        toast.success('Subscribed to push notifications!');
      }
    } catch (error) {
      console.error('Push toggle error:', error);
      if (error.message?.includes('denied')) {
        setPushPermission('denied');
        toast.error('Notification permission denied. Please enable in browser settings.');
      } else {
        toast.error(error.message || 'Failed to toggle push notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/test-push`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Test notification sent! Check your browser.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test notification');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTestingPush(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: profile.name,
          contact_number: profile.contact_number
        })
      });
      
      if (response.ok) {
        toast.success('Profile updated');
        if (profile.name !== user?.name) {
          setUser({ ...user, name: profile.name });
        }
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/profile/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profile.preferences)
      });
      
      if (response.ok) {
        toast.success('Preferences saved');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail || profile.email })
      });
      
      if (response.ok) {
        const data = await response.json();
        setOtpSent(true);
        toast.success('OTP sent to your email');
        // Show debug OTP in development
        if (data._debug_otp) {
          toast.info(`Debug OTP: ${data._debug_otp}`, { duration: 10000 });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email: otpEmail || profile.email,
          otp 
        })
      });
      
      if (response.ok) {
        toast.success('OTP verified successfully');
        setOtp('');
        setOtpSent(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Profile & Settings</h1>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="h-4 w-4 mr-1" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="h-4 w-4 mr-1" />
            Security
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">
            <Bell className="h-4 w-4 mr-1" />
            Preferences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <GroupBox label="Personal Information" className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  value={profile.email}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input 
                  value={profile.contact_number}
                  onChange={(e) => setProfile({...profile, contact_number: e.target.value})}
                  placeholder="+91 XXXXXXXXXX"
                  data-testid="profile-phone-input"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Input 
                  value={user?.role || 'user'}
                  disabled
                  className="bg-muted capitalize"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSaveProfile} disabled={loading} data-testid="save-profile-btn">
                <Save className="h-4 w-4 mr-1" />
                Save Profile
              </Button>
            </div>
          </GroupBox>

          {sessionInfo && (
            <GroupBox label="Workspace Information" className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Workspace Name</Label>
                  <Input value={sessionInfo.tenant?.name || ''} disabled className="bg-muted" />
                </div>
                <div>
                  <Label>Plan</Label>
                  <Input value={sessionInfo.tenant?.plan || 'free'} disabled className="bg-muted capitalize" />
                </div>
                <div>
                  <Label>Active Sessions</Label>
                  <Input 
                    value={`${sessionInfo.session_info?.active_sessions} / ${sessionInfo.session_info?.max_concurrent}`} 
                    disabled 
                    className="bg-muted" 
                  />
                </div>
                <div>
                  <Label>Remaining Slots</Label>
                  <Input 
                    value={sessionInfo.session_info?.remaining_slots} 
                    disabled 
                    className="bg-muted" 
                  />
                </div>
              </div>
            </GroupBox>
          )}
        </TabsContent>

        <TabsContent value="security" className="mt-4 space-y-4">
          <GroupBox label="Two-Factor Authentication (Email OTP)" className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Add an extra layer of security by enabling email-based OTP verification.
            </p>
            
            <div className="space-y-4">
              <div>
                <Label>Email for OTP</Label>
                <Input 
                  value={otpEmail || profile.email}
                  onChange={(e) => setOtpEmail(e.target.value)}
                  placeholder="Enter email address"
                  data-testid="otp-email-input"
                />
              </div>
              
              {!otpSent ? (
                <Button onClick={handleRequestOtp} disabled={loading} data-testid="request-otp-btn">
                  <Key className="h-4 w-4 mr-1" />
                  Request OTP
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Enter OTP</Label>
                    <Input 
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      data-testid="otp-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6} data-testid="verify-otp-btn">
                      Verify OTP
                    </Button>
                    <Button variant="outline" onClick={() => setOtpSent(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </GroupBox>

          <GroupBox label="Session Management" className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Your current session will expire in 7 days. You can log out from all devices if needed.
            </p>
            <Button variant="destructive" size="sm">
              Log Out All Devices
            </Button>
          </GroupBox>
        </TabsContent>

        <TabsContent value="preferences" className="mt-4 space-y-4">
          {/* Push Notifications Section */}
          <GroupBox label="Push Notifications" className="p-4">
            <div className="space-y-4">
              {!pushSupported ? (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600">
                  <p className="text-sm">Push notifications are not supported in this browser.</p>
                </div>
              ) : pushPermission === 'denied' ? (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
                  <p className="text-sm">
                    Push notifications are blocked. Please enable them in your browser settings:
                    <br />
                    Click the lock icon in the address bar → Site settings → Notifications → Allow
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {pushSubscribed ? (
                      <BellRing className="h-5 w-5 text-primary" />
                    ) : (
                      <BellOff className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium">Browser Push Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        {pushSubscribed 
                          ? 'You will receive notifications even when not on the site'
                          : 'Enable to receive notifications when you\'re away'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pushSubscribed && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleTestPush}
                        disabled={testingPush}
                        data-testid="test-push-btn"
                      >
                        {testingPush ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1" />
                            Test
                          </>
                        )}
                      </Button>
                    )}
                    <Button 
                      variant={pushSubscribed ? "outline" : "default"}
                      onClick={handleTogglePush}
                      disabled={pushLoading}
                      data-testid="toggle-push-btn"
                    >
                      {pushLoading ? 'Loading...' : pushSubscribed ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </GroupBox>

          <GroupBox label="Application Preferences" className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-muted-foreground">Use dark theme</p>
                </div>
                <Switch 
                  checked={profile.preferences.dark_mode}
                  onCheckedChange={(v) => setProfile({
                    ...profile, 
                    preferences: {...profile.preferences, dark_mode: v}
                  })}
                  data-testid="pref-dark-mode"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notifications</p>
                  <p className="text-sm text-muted-foreground">Enable push notifications</p>
                </div>
                <Switch 
                  checked={profile.preferences.notifications_enabled}
                  onCheckedChange={(v) => setProfile({
                    ...profile, 
                    preferences: {...profile.preferences, notifications_enabled: v}
                  })}
                  data-testid="pref-notifications"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sound Effects</p>
                  <p className="text-sm text-muted-foreground">Play sounds on transactions</p>
                </div>
                <Switch 
                  checked={profile.preferences.sound_enabled}
                  onCheckedChange={(v) => setProfile({
                    ...profile, 
                    preferences: {...profile.preferences, sound_enabled: v}
                  })}
                  data-testid="pref-sound"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Auto Refresh</p>
                  <p className="text-sm text-muted-foreground">Automatically refresh data</p>
                </div>
                <Switch 
                  checked={profile.preferences.auto_refresh}
                  onCheckedChange={(v) => setProfile({
                    ...profile, 
                    preferences: {...profile.preferences, auto_refresh: v}
                  })}
                  data-testid="pref-auto-refresh"
                />
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSavePreferences} disabled={loading} data-testid="save-preferences-btn">
                <Save className="h-4 w-4 mr-1" />
                Save Preferences
              </Button>
            </div>
          </GroupBox>
        </TabsContent>
      </Tabs>
    </div>
  );
}
